import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { COLOURS } from "../shared";

type ToggleProps = {
  toggled?: boolean;
  onClick?: (checked: boolean) => void;
  disabled?: boolean;
};

const ToggleWrapper = styled.label<{ disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  cursor: ${(p) => (p.disabled ? "not-allowed" : "pointer")};
  gap: 0.8rem;
  user-select: none;
  position: relative;
`;

const ToggleContainer = styled.div<{
  checked: boolean;
  disabled?: boolean;
}>`
  position: relative;
  width: 3.6rem;
  height: 2rem;
  border: 1px solid
    ${(p) =>
      p.disabled
        ? "transparent"
        : p.checked
          ? COLOURS.toggleOnBorderDefault
          : COLOURS.toggleOffBorderDefault};
  background-color: ${(p) =>
    p.disabled
      ? COLOURS.toggleDisabledBackground
      : p.checked
        ? COLOURS.toggleOnBackgroundDefault
        : COLOURS.toggleOffBackgroundDefault};
  border-radius: 10px;
  transition:
    background-color 0.3s ease,
    border-color 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 0.3rem;

  // Hover/focus/active
  ${(p) =>
    !p.disabled &&
    `
  &:hover,
  &:focus,
  &:focus-visible {
    outline: none;
    background-color: ${
      p.checked
        ? COLOURS.toggleOnBackgroundHoverFocus
        : COLOURS.toggleOffBackgroundHoverFocus
    };
    border-color: ${
      p.checked
        ? COLOURS.toggleOnBorderHoverFocus
        : COLOURS.toggleOffBorderDefault
    };
  }

  &:active {
    background-color: ${
      p.checked
        ? COLOURS.toggleOnBackgroundActive
        : COLOURS.toggleTransitionBackground
    };
    border-color: ${
      p.checked ? COLOURS.toggleOnBorderActive : COLOURS.toggleTransitionBorder
    };
  }
`}
`;

const ToggleHandle = styled.div<{ checked: boolean }>`
  position: absolute;
  top: 0.1rem;
  left: ${(p) => (p.checked ? "1.7rem" : "0.1rem")};
  width: 1.6rem;
  height: 1.6rem;
  background-color: ${COLOURS.toggleHandleBackground};
  border-radius: 50%;
  transition: left 0.3s ease;
  pointer-events: none;
`;

const IconWrapper = styled.div<{
  position: "left" | "right";
  visible: boolean;
  disabled?: boolean;
  checked?: boolean;
}>`
  width: 1.2rem;
  height: 1.2rem;
  opacity: ${(p) => (p.visible ? 1 : 0)};
  transition: opacity 0.2s ease;
  pointer-events: none;

  svg {
    width: 1.2rem;
    height: 1.2rem;
    color: ${(p) => {
      if (p.disabled) return COLOURS.toggleDisabledIcon;
      if (p.position === "left" && p.checked) return COLOURS.toggleOnIcon;
      if (p.position === "right" && !p.checked) return COLOURS.toggleOffIcon; // x when off
      return "transparent";
    }};
  }
`;

export const Toggle: React.FC<ToggleProps> = ({
  toggled = false,
  onClick,
  disabled = false,
}) => {
  const [checked, setChecked] = useState(toggled);

  useEffect(() => {
    if (disabled) {
      setChecked(false); // reset to off when disabled
    } else {
      setChecked(toggled); // sync with external prop otherwise
    }
  }, [toggled, disabled]);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (disabled) return;
    const next = !checked;
    setChecked(next);
    onClick?.(next);
  };

  return (
    <ToggleWrapper disabled={disabled} onClick={handleClick} tabIndex={0}>
      <ToggleContainer checked={checked} disabled={disabled}>
        <IconWrapper
          position="left"
          visible={checked}
          disabled={disabled}
          checked={checked}
        >
          <svg viewBox="0 0 448 512">
            <path
              fill="currentColor"
              d="M441 103c9.4 9.4 9.4 24.6 0 33.9L177 401c-9.4 9.4-24.6 9.4-33.9 0L7 265c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l119 119L407 103c9.4-9.4 24.6-9.4 33.9 0z"
            />
          </svg>
        </IconWrapper>

        <IconWrapper
          position="right"
          visible={!checked}
          disabled={disabled}
          checked={checked}
        >
          <svg viewBox="0 0 384 512">
            <path
              fill="currentColor"
              d="M345 137c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-119 119L73 103c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l119 119L39 375c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l119-119L311 409c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-119-119L345 137z"
            />
          </svg>
        </IconWrapper>

        <ToggleHandle checked={checked} />
      </ToggleContainer>
    </ToggleWrapper>
  );
};
